/**
 * Flush pending outbox actions when connectivity returns.
 */

import {
  listPendingOutbox,
  markOutboxSyncing,
  markOutboxSynced,
  markOutboxFailed,
  markOutboxTerminal,
  markOutboxHeld,
  recoverStuckSyncing,
  currentOutboxUserId,
  isLegacyOwnerlessRow,
} from './outbox.service.js';
import { executeOutboxAction } from './actionHandlers.js';

let flushing = false;
let listeners = new Set();

export function subscribeSyncEngine(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(event) {
  for (const fn of listeners) {
    try {
      fn(event);
    } catch (err) {
      console.warn('[syncEngine] listener error:', err);
    }
  }
}

// Codes 4xx qui restent transitoires : session expirée (401, l'action repart
// après reconnexion), délai (408), trop tôt / trop de requêtes (425, 429).
const RETRYABLE_4XX = new Set([401, 408, 425, 429]);
// 409 conçus pour être retentés : un « reçu » rejoué avant l'« envoyé » du
// payeur (fondsEnqueue.js).
const RETRYABLE_ERROR_CODES = new Set(['CONFIRM_BEFORE_SENT']);

/**
 * F-07 — un refus DÉFINITIF du serveur (4xx hors liste ci-dessus : demande
 * annulée → 409 INVALID_TRANSITION, coupe-circuit → 403 FONDS_DISABLED,
 * parcours verrouillé → 409, garde 400/404/422…) ne se résoudra pas en
 * rejouant : on arrête tout de suite au lieu de 5 retentatives muettes.
 * Réseau (pas de réponse HTTP), 5xx et erreurs locales restent retentables.
 */
export function isTerminalSyncError(err) {
  const status = err?.response?.status;
  if (typeof status !== 'number' || status < 400 || status >= 500) return false;
  if (RETRYABLE_4XX.has(status)) return false;
  const code = err?.response?.data?.error_code;
  if (code && RETRYABLE_ERROR_CODES.has(code)) return false;
  return true;
}

/**
 * F-29 — un 401 arrivé jusqu'ici veut dire « plus de session » (l'intercepteur
 * a déjà tenté le rafraîchissement) : on arrête le passage sans consommer
 * d'essai, la file attend la reconnexion.
 */
function isSessionLost(err) {
  return err?.response?.status === 401;
}

/**
 * F-35 — une ligne héritée (mise en file avant le rattachement au propriétaire)
 * refusée en 403 appartient sans doute à un autre compte de l'appareil : elle
 * reste en attente, sans essai consommé, pour son vrai propriétaire. Le
 * coupe-circuit fonds (FONDS_DISABLED) reste un refus définitif (F-07).
 */
function isForeignLegacyRefusal(action, err) {
  if (!isLegacyOwnerlessRow(action)) return false;
  if (err?.response?.status !== 403) return false;
  return err?.response?.data?.error_code !== 'FONDS_DISABLED';
}

function refusedErrorCode(err) {
  return err?.response?.data?.error_code || `HTTP_${err?.response?.status}`;
}

// `userId` : propriétaire dont on rejoue la file (par défaut l'utilisateur
// connecté). Les actions d'un autre compte ne sont jamais rejouées (F-35).
export async function flushSyncQueue({ beneficiaireId = null, userId = currentOutboxUserId() } = {}) {
  if (flushing) return { flushed: 0, failed: 0, refused: 0, skipped: true };
  flushing = true;
  let flushed = 0;
  let failed = 0;
  let refused = 0;
  let halted = false;
  notifyListeners({ type: 'start' });

  try {
    // Recover any action orphaned in SYNCING by a previously interrupted flush.
    await recoverStuckSyncing();
    const pending = await listPendingOutbox(beneficiaireId, userId ?? null);
    for (const action of pending) {
      try {
        await markOutboxSyncing(action.id);
        await executeOutboxAction(action);
        await markOutboxSynced(action.id);
        flushed += 1;
      } catch (err) {
        const msg = err?.response?.data?.error || err?.message || 'Erreur de synchronisation';
        if (isSessionLost(err)) {
          await markOutboxHeld(action.id);
          halted = true;
          console.warn('[syncEngine] session absente — rejeu suspendu');
          break;
        }
        if (isForeignLegacyRefusal(action, err)) {
          await markOutboxHeld(action.id);
          console.warn('[syncEngine] action héritée refusée à ce compte — gardée en attente:', action.type);
          continue;
        }
        if (isTerminalSyncError(err)) {
          refused += 1;
          const errorCode = refusedErrorCode(err);
          await markOutboxTerminal(action.id, msg, errorCode);
          console.warn('[syncEngine] action refused:', action.type, errorCode, msg);
          // Les écrans concernés (fonds, parcours) rechargent l'état serveur pour
          // défaire l'affichage optimiste posé hors ligne.
          notifyListeners({
            type: 'refused',
            outboxId: action.id,
            actionType: action.type,
            demandeId: action.payload?.demandeId ?? null,
            beneficiaireId: action.beneficiaireId ?? action.payload?.beneficiaireId ?? null,
            etapeId: action.payload?.etapeId ?? null,
            errorCode,
            message: msg,
          });
        } else {
          failed += 1;
          await markOutboxFailed(action.id, msg);
          console.warn('[syncEngine] action failed:', action.type, msg);
        }
      }
    }
  } finally {
    flushing = false;
    notifyListeners({ type: 'end', flushed, failed, refused, halted });
  }

  return { flushed, failed, refused, halted };
}

export function isSyncFlushing() {
  return flushing;
}
